import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges } from '../../utils/deals';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { useDeals } from '../../context/DealsContext';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('vp', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const cls = all.filter(d => d.status === 'Closed');
  const onh = all.filter(d => d.status === 'On Hold');
  const can = all.filter(d => d.status === 'Cancelled');
  const scs = new Set(ip.map(d => d.sc)).size;
  const pipelineOppGnarr = sumUniqueBy(all, d => d.opp_gnarr);
  const ages = validAges(all);
  const titleLbl = state.sub && state.sub !== 'All' ? state.sub : state.r;
  const crumb = state.sub && state.sub !== 'All';

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
  }> = {};
  all.forEach(d => {
    const key = showSub ? d.sub_region : d.region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: d.region
    };
    regMap[key].book++;
    if (d.status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += d.gnarr;
    } else if (d.status === 'Closed') regMap[key].closed++;else if (d.status === 'On Hold') regMap[key].onhold++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const ALLOWED_REGIONS = new Set(['AMER', 'EMEA', 'JAPAC', '']);
  const regRows = Object.entries(regMap).filter(([reg]) => showSub || ALLOWED_REGIONS.has(reg)).sort((a, b) => b[1].ip - a[1].ip);

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(d => {
    if (!cqMap[d.close_q]) cqMap[d.close_q] = {
      n: 0,
      g: 0
    };
    cqMap[d.close_q].n++;
    cqMap[d.close_q].g += d.gnarr;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-352">
      {/* Metrics */}
      <div className="mrow" data-id="element-353">
        <MetricCard label="In-progress deals" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-354" />
        <MedianAgeCard ages={ages} data-id="element-355" />
        <MetricCard label="Closed this period" value={cls.length} colorClass="c-green" data-id="element-356" />
        <GnarrInfluencedCard value={pipelineOppGnarr} data-id="element-357" />
      </div>

      {/* Trend chart */}
      <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-359">{state.sub}</span>}</>} data-id="element-358" />
      <TrendChart region={state.r} sub={state.sub} status={state.status} deals={deals} data-id="element-360" />

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-362">{state.sub}</span>}</>} data-id="element-361" />
      <div className="tw" data-id="element-363">
        <div className="sub" data-id="element-364">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <table className="tbl" data-id="element-365">
          <thead data-id="element-366">
            <tr data-id="element-367">
              <th style={{
              width: '14%'
            }} data-id="element-368">Region</th>
              <th style={{
              width: '26%'
            }} data-id="element-369">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-370">Pipeline GNARR</th>
              <th style={{
              width: '12%'
            }} data-id="element-371">Closed</th>
              <th style={{
              width: '12%'
            }} data-id="element-372">Total book</th>
              <th style={{
              width: '10%'
            }} data-id="element-373">On hold</th>
              <th style={{
              width: '10%'
            }} data-id="element-374">Sub-region</th>
            </tr>
          </thead>
          <tbody data-id="element-375">
            {regRows.map(([reg, d]) => <tr key={reg} onClick={() => onDrillRegion(d.parent || reg, showSub ? reg : '')} data-id="element-376">
                <td data-id="element-377">
                  <span className="rdot" style={{
                background: RDOTS[reg] || '#888'
              }} data-id="element-378" />
                  {reg || 'No DR'}
                </td>
                <td data-id="element-379">
                  <div className="pbg" style={{
                width: 90
              }} data-id="element-380">
                    <div className="pf" style={{
                  width: `${Math.round(d.ip / maxIP * 100)}%`
                }} data-id="element-381" />
                  </div>
                  <strong data-id="element-382">{d.ip}</strong>
                </td>
                <td data-id="element-383">{fmtG(d.gnarr)}</td>
                <td style={{
              color: d.closed > 0 ? '#15803D' : '#344054',
              fontWeight: d.closed > 0 ? 600 : 400
            }} data-id="element-384">{d.closed}</td>
                <td data-id="element-385">{d.book}</td>
                <td data-id="element-386">{d.onhold}</td>
                <td data-id="element-387">{showSub ? <span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-388">{d.parent}</span> : '—'}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Book status + CQ grid */}
      {/* <div className="br2">
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 8px' }}>
            <div className="shb" />
            <span className="sht">Book status mix</span>
          </div>
          <div className="book-bar">
            {segs.map(s => (
              <div key={s.l} style={{ width: `${Math.round(s.v / total * 100)}%`, height: '100%', background: s.c }} />
            ))}
          </div>
          <div className="bleg">
            {segs.map(s => (
              <span key={s.l}>
                <span className="bldot" style={{ background: s.c }} />
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 6px' }}>
            <div className="shb" />
            <span className="sht">In progress by close quarter</span>
          </div>
          <div className="cqg">
            {cqShow.map((q, i) => (
              <div key={q} className="cqc">
                <div className="cql">{q}</div>
                <div className="cqn">{cqMap[q].n}</div>
                <div className="cqs">in-progress deals</div>
                <div className="cqs">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{ background: qColors[i] || '#4f9cf9' }} />
              </div>
            ))}
          </div>
        </div>
       </div> */}

    </div>;
}